import { useState, useEffect, useCallback } from 'react'
import debounce from 'lodash/debounce'

// 创建同构的 useLayoutEffect
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useEffect : useEffect

type WidthType = 'window' | 'body'

// 默认的初始宽度值（可以根据实际需求调整）
const DEFAULT_WIDTH = 1440

/**
 * 监听窗口宽度变化的自定义 Hook
 * @param type - 宽度类型，可选 'window' | 'body'
 * @param delay - 防抖延迟时间(ms)，默认 200ms
 * @returns 当前宽度和屏幕类型
 */
const useWidth = (type: WidthType = 'window', delay: number = 200) => {
  const getWidth = () => {
    if (typeof window === 'undefined') return DEFAULT_WIDTH
    return type === 'window' ? window.innerWidth : document.body.clientWidth
  }

  const [width, setWidth] = useState(DEFAULT_WIDTH)

  const handleResize = useCallback(
    debounce(() => {
      setWidth(getWidth())
    }, delay),
    [delay, type]
  )

  // 使用 useIsomorphicLayoutEffect 替代 useEffect
  useIsomorphicLayoutEffect(() => {
    // 初始化时设置实际宽度
    setWidth(getWidth())

    window.addEventListener('resize', handleResize)

    return () => {
      handleResize.cancel()
      window.removeEventListener('resize', handleResize)
    }
  }, [handleResize])

  // 根据宽度计算屏幕类型
  const screenType: 'pc' | 'pad' | 'phone' = width > 1440 ? 'pc' : width > 768 ? 'pad' : 'phone'

  return { width, screenType }
}

export default useWidth
